import { debitNoteApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetDebitNoteParamsI,
  ListDebitNoteI,
  OneDebitNoteForFrontI,
  NewDebitNoteI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getDebitNotes = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  typeCode,
  dateFrom,
  dateTo,
}: GetDebitNoteParamsI): Promise<ServiceResponseI<DataI<ListDebitNoteI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId) params.append("branchId", branchId);
    if (typeCode) params.append("typeCode", typeCode);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo)   params.append("dateTo",   dateTo);

    const { data } = await debitNoteApi.get<ServiceResponseI<DataI<ListDebitNoteI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const debitNoteById = async (
  id: string,
): Promise<ServiceResponseI<OneDebitNoteForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await debitNoteApi.get<ServiceResponseI<OneDebitNoteForFrontI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const debitNotePost = async (
  payload: NewDebitNoteI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await debitNoteApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const debitNoteAnnul = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await debitNoteApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useDebitNotes = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  typeCode,
  dateFrom,
  dateTo,
}: GetDebitNoteParamsI) => {
  const debitNotesQuery = useQuery(
    ["debit-notes", { pagination, sorting, globalFilter, branchId, typeCode, dateFrom, dateTo }],
    () => getDebitNotes({ pagination, sorting, globalFilter, branchId, typeCode, dateFrom, dateTo }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData:     true,
    },
  );

  return { debitNotesQuery };
};